import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Reveal, SectionHead } from './Reveal';
import { BadgeCheck, ArrowUpRight } from 'lucide-react';
import { WA_DEFAULT_LINK } from '../lib/lead';

const POINTS = [
  'Licensed Traffic Advocates & Lok Adalat Practitioners',
  'Fastest Settlement Turnaround — Most Cases in 24–48 Hours',
  '100% Money-Back Assurance If Your Fine Is Not Legally Settled',
  'Transparent Flat Fee — No Hidden Charges, Ever',
];

export default function WhyUs() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  return (
    <section id="why-us" ref={ref} className="py-20 lg:py-28 bg-white overflow-hidden" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden spotlight-frame aspect-[4/5] max-w-md" data-testid="why-us-image">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000"
                alt="Lady justice statue — legal representation for traffic challans"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <motion.div
                initial={{ opacity: 0, y: 24, rotate: -2 }}
                whileInView={{ opacity: 1, y: 0, rotate: -2 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, delay: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
                className="absolute bottom-6 -left-3 sm:-left-6 rounded-xl bg-brand-blue text-white px-5 py-4 shadow-2xl shadow-brand-blue/40"
                data-testid="why-us-stat-badge"
              >
                <span className="block font-display text-3xl font-bold leading-none">10,000+</span>
                <span className="mt-1 block text-[11px] font-bold uppercase tracking-widest text-brand-orange">
                  Challans Disposed
                </span>
              </motion.div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <SectionHead
            eyebrow="Why Settalkar"
            title="The Legal Desk India Trusts With Its Record"
            sub="We are not a paid-portal trick. We are a legal team that walks your challan through the exact statutory process — Virtual Court, Lok Adalat, and Parivahan."
          />
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {POINTS.map((p, i) => (
              <Reveal key={p} delay={0.1 + i * 0.08}>
                <li
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-[#F8FAFC] px-5 py-4 h-full hover:border-brand-blue/30 transition-colors"
                  data-testid={`why-us-point-${i + 1}`}
                >
                  <BadgeCheck size={20} strokeWidth={2.4} className="shrink-0 mt-0.5 text-brand-orange" />
                  <span className="text-sm font-semibold text-brand-ink leading-snug">{p}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.4}>
            <a
              href={WA_DEFAULT_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-9 inline-flex items-center gap-2 rounded-xl bg-brand-orange hover:bg-brand-orangedark text-white font-bold px-7 py-3.5 shadow-xl shadow-orange-500/25 transition-all hover:-translate-y-0.5"
              data-testid="why-us-whatsapp-cta"
            >
              Talk to a Legal Expert <ArrowUpRight size={17} strokeWidth={2.5} />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
